import React, { useEffect } from 'react';
import { FabricationContainer } from '../styles';
import {
  ExtraItensBlock,
  AddExtraItemIcon,
  AddExtraItemContainer,
  ButtonContainer,
  NewExtraItensContentBlock
} from './styles';
import { EFabricationRoutes } from '../fabrication.routes';
import PieceTypesReleaseToggle from './PieceTypes/releaseToggleIndex';
import { ApplicationState } from '../../../../store';
import { useDispatch, useSelector } from 'react-redux';
import {
  DEFAULT_WIRE_ITEM,
  EFabricationOrderSteps,
  IConstructionIdentificationState,
  IExtraItensState,
  IFabricationOrderExtraItem
} from '../../../../store/ducks/fabrication-purchase-order/types';
import ExtraItensContainer from './ExtraItensContainer/indexExtraItensContainer';
import { isAllFieldsValidInExtraItens } from '../fabricationOrderFieldsValidations';
import {
  loadPieceTypes,
  loadSteelTypes,
  loadWireTypes
} from '../../../../store/ducks/fabrication/actions';
import { IFabricationContractState } from '../../../../store/ducks/fabrication-contracts/types';

import { Title } from '@gerdau/hefesto/Title';
import { Button } from '@gerdau/hefesto/Button';
import WireItensTable from './WireItensTable';
import { getTaggingInformationScreen } from '../../../../utils/Tagging';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import moment from 'moment';

interface IExtraItensProps {
  selectedContractNumber: string;
  contractsState: IFabricationContractState;
  extraItensState: IExtraItensState;
  setExtraItensState(extraItensState: IExtraItensState): void;
  goToFabricationRoute(route: EFabricationRoutes): void;
  onInitFabricationRoute(fabricationOrderStep: EFabricationOrderSteps): void;
  hasAnyProjectSelected: boolean;
  comingFromBuyAgain?: boolean;
  constructionIdentificationState: IConstructionIdentificationState;
}

const ExtraItens: React.FC<IExtraItensProps> = ({
  selectedContractNumber,
  contractsState,
  extraItensState,
  setExtraItensState,
  goToFabricationRoute,
  onInitFabricationRoute,
  hasAnyProjectSelected,
  constructionIdentificationState
}) => {
  const dispatch = useDispatch();

  const { screenName, itemDescription } = getTaggingInformationScreen(
    'extra_itens_add_item_annealed_steel'
  );

  const onClickAddItem = () => {
    dispatch(
      sendTagging({
        screenName,
        itemName: 'button:adicionar_item',
        event: 'click',
        itemDescription: `${itemDescription}_arameRecozidoAdicionarItem`
      })
    );

    setExtraItensState({
      ...extraItensState,
      annealedWires: [...extraItensState.annealedWires, DEFAULT_WIRE_ITEM]
    });
  };

  const pieceTypesState = useSelector(
    (appState: ApplicationState) => appState.fabrication.pieceTypesState
  );
  const steelTypesState = useSelector(
    (appState: ApplicationState) => appState.fabrication.steelTypesState
  );
  const wireTypesState = useSelector(
    (appState: ApplicationState) => appState.fabrication.annealedWireTypesState
  );

  useEffect(() => {
    dispatch(
      sendTagging({
        screenName: 'LG:Comprar:CorteEDobra:ItensExtras',
        event: 'page_view',
        page: window.location.pathname
      })
    );
  }, []);
  const onAddExtraItemFromPieceTypes = (
    extraItem: IFabricationOrderExtraItem
  ) => {
    let otherPieces = extraItensState.otherPieces;
    if (otherPieces.length === 1 && !otherPieces[0].pieceTypeId) {
      otherPieces = [extraItem];
    } else {
      otherPieces = [...otherPieces, extraItem];
    }

    setExtraItensState({
      ...extraItensState,
      otherPieces
    });
  };

  const loadPieceAndSteelIfNeed = () => {
    if (!pieceTypesState) {
      dispatch(loadPieceTypes());
    }

    if (!steelTypesState) {
      dispatch(loadSteelTypes());
    }
  };

  const handleLoadWireTypes = () => {
    const contract = getSelectedContract();
    if (contract) {
      const annealedWireIds =
        contract.annealedWireType?.map((at) => at.annealedWireId) || [];
      dispatch(loadWireTypes(annealedWireIds, selectedContractNumber));
    }
  };

  const loadWireTypesIfNeed = () => {
    if (
      selectedContractNumber !== undefined &&
      ((!wireTypesState.wireTypes &&
        !wireTypesState.loading &&
        !wireTypesState.error) ||
        wireTypesState.selectedContractNumber !== selectedContractNumber)
    ) {
      handleLoadWireTypes();
    }
  };

  const getSelectedContract = () => {
    const selectedContract = contractsState.contracts?.find(
      (contr) => contr.contractNumber === selectedContractNumber
    );
    return selectedContract;
  };

  useEffect(() => {
    onInitFabricationRoute(EFabricationOrderSteps.EXTRA_ITENS);
    loadPieceAndSteelIfNeed();
  }, []);

  useEffect(() => {
    if (!wireTypesState.loading || !wireTypesState.wireTypes) {
      loadWireTypesIfNeed();
    }
  });

  const canShowToNextStep = () => {
    return isAllFieldsValidInExtraItens(
      hasAnyProjectSelected,
      extraItensState,
      pieceTypesState,
      steelTypesState,
      wireTypesState
    );
  };

  const onCancelAndReturn = () => {
    dispatch(
      sendTagging({
        screenName,
        itemName: 'button:cancelar',
        event: 'click',
        itemDescription: `${itemDescription}_cancelar`,
        orderCliente: '',
        contratoObra: selectedContractNumber,
        filterInfo: `#cttObra:${selectedContractNumber}#orderCliente:${
          constructionIdentificationState?.orderNumber
        }#dtDesejada:${
          constructionIdentificationState?.desiredDate
            ? moment(constructionIdentificationState?.desiredDate)
                .subtract(1, 'days')
                .format('YYYY/MM/DD')
            : 'not_set'
        }`
      })
    );
    goToFabricationRoute(EFabricationRoutes.DETAILING_PROJECT);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToDetail = () => {
    dispatch(
      sendTagging({
        screenName,
        itemName: 'button:avancar_para_revisao',
        event: 'click',
        itemDescription: `${itemDescription}_avancarParaRevisao`,
        orderCliente: '',
        contratoObra: selectedContractNumber,
        filterInfo: `#cttObra:${selectedContractNumber}#orderCliente:${
          constructionIdentificationState?.orderNumber
        }#dtDesejada:${
          constructionIdentificationState?.desiredDate
            ? moment(constructionIdentificationState?.desiredDate)
                .subtract(1, 'days')
                .format('YYYY/MM/DD')
            : 'not_set'
        }`
      })
    );
    goToFabricationRoute(EFabricationRoutes.CHECKOUT);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <FabricationContainer isFacelift={false}>
      <ExtraItensBlock>
        <>
          <Title level={2}>Tipo de peça</Title>
          <NewExtraItensContentBlock className="bottom-bordered">
            {!pieceTypesState?.loading && !pieceTypesState?.error && (
              <span className="subtitle">
                Clique no item que deseja adicionar:
              </span>
            )}
            <PieceTypesReleaseToggle
              onAddItem={onAddExtraItemFromPieceTypes}
              pieceTypesState={pieceTypesState}
              steelTypesState={steelTypesState}
            />
          </NewExtraItensContentBlock>
        </>

        <>
          {/* Seleçao de itens extras  */}
          <ExtraItensContainer
            pieceTypesState={pieceTypesState}
            steelTypesState={steelTypesState}
            extraItensState={extraItensState}
            setExtraItensState={setExtraItensState}
          />
        </>
      </ExtraItensBlock>
      <ExtraItensBlock>
        <Title level={2}>Arame recozido</Title>
        {wireTypesState.wireTypes?.length !== 0 &&
        !wireTypesState.error &&
        !wireTypesState.loading ? (
          <span className="subtitle">
            Caso o tipo de arame desejado não esteja disponível, entre em
            contato com a Gerdau.
          </span>
        ) : (
          <span className="subtitle">
            * Não há arames disponíveis para o contrato
            <b> {selectedContractNumber}</b>. Entre em contato com a Gerdau.
          </span>
        )}
      </ExtraItensBlock>
      <AddExtraItemContainer>
        <Button
          name="addExtraItemButton"
          onClick={onClickAddItem}
          variant="secondary"
          icon={<AddExtraItemIcon />}
          disabled={!wireTypesState.wireTypes?.length}
        >
          Adicionar item
        </Button>
      </AddExtraItemContainer>
      <WireItensTable
        extraItensState={extraItensState}
        setExtraItensState={setExtraItensState}
        wireTypesState={wireTypesState}
      />
      <ButtonContainer fixed>
        <Button
          type="button"
          variant="secondary"
          labelPosition="center"
          name="cancelAndReturnButton"
          onClick={() => onCancelAndReturn()}
        >
          Voltar
        </Button>
        <Button
          name="goToDetailButton"
          onClick={() => goToDetail()}
          variant="primary"
          disabled={!canShowToNextStep()}
        >
          Avançar para revisão do pedido
        </Button>
      </ButtonContainer>
    </FabricationContainer>
  );
};

export default ExtraItens;
